import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { PointerLockControls } from "three/examples/jsm/controls/PointerLockControls.js";
import { XRControllerModelFactory } from "three/examples/jsm/webxr/XRControllerModelFactory.js";
import { OculusHandModel } from "three/examples/jsm/webxr/OculusHandModel.js";
import { Container, Root, Text } from "@pmndrs/uikit";
import {
  joints,
  createHand,
  createController,
  createAvatar,
  clearGroup,
  createElement,
  loader,
  cursorGroup,
  createTunnelVignette,
  getAtomString,
  collideMoleculeWithBothHands,
} from "./utils.js";

import { store } from "../store";
import {
  selectIsConnected,
  selectColor,
  selectAvailableElements,
  selectScaling,
  selectGrabbingPermission,
  selectMultipleGrabbingPermission,
  selectisInVR,
  selectisInTrialMode,
  selectisTyping,
  selectIsShowingInfoModal,
  selectIsShowingHelpOverlay,
  selectSessionType,
  selectStickRadiusScale,
  selectRenderScale,
  selectSimState,
  selectPdb,
  selectMyId,
  selectisMinimizing,
  selectIsOrbit,
} from "../store/selectors.js";
import { sceneSlice } from "../store/sceneSlice.js";
import { sessionSlice } from "../store/sessionSlice.js";

let isRightControllerConnected = false;
let isLeftControllerConnected = false;
let isRHand = false;
let isLHand = false;
let isColorUpdated = false;
let controlEventCounter = 0;
let initialInputSource = null;

let atoms;
let bondData;
let bonds;
let userMeshes = [];
let elementMeshes = [];
const grabbingControllers = [];
const grabbedMeshes = [];
let rubberMeshes = [];

// Gimbal state tracking
let gimbal = null;
let isGimbalGrabbed = false;
let gimbalGrabbingController = null;
let gimbalOwner = null;
let gimbalGrabSphere = null;

let counter = 0;
let counter2 = 0;
let debugCounter = 0;
let lastTime = 0;
let frameCounter = 0;
const _PI_2 = Math.PI / 2;
const minPolarAngle = 0;
const maxPolarAngle = Math.PI;

const SIMPLE = 0.12;
const DOUBLE = 0.2;
const TRIPLE = 0.25;
const HOVER_DISTANCE = 0.05;
const HAND_TRIGGER_DISTANCE = 0.03;

const bondMaterial = new THREE.MeshLambertMaterial({ color: "white" });
const sphereMaterial = new THREE.MeshLambertMaterial({
  color: "white", //0xffffff,
  transparent: true,
  opacity: 0.3,
});

const rubberGeometry = new THREE.SphereGeometry(0.05, 16, 16);
const rubberMaterial = new THREE.MeshLambertMaterial({
  color: "yellow",
});

const sphereGeometry = new THREE.SphereGeometry(1, 16, 16);
const textureLoader = new THREE.TextureLoader();
const tmpMatrix = new THREE.Matrix4();

let scene;
let camera;
let renderer;
let controls;
let wasdControls;
let controller1;
let controller2;
let hand1;
let hand2;
let controllerGrip1;
let controllerGrip2;
let slideMat;
let environment;
let tunnelVignette;
let toastAnchor;
let toastRoot;
let toastMsg;
let label1Anchor;
let label1Root;
let label1Msg;
let label2Anchor;
let label2Root;
let label2Msg;
let menuAnchor;
let menuRoot;
let wristButtonAnchor;
let wristButton;
let buttonIncreaseT;
let buttonDecreaseT;
let buttonPlayMD;
let buttonStopMD;
let buttonClose;
let buttonZoomIn;
let buttonZoomOut;
let buttonWritePDB;
let buttons;
let prev = null;
let sound;

let isMouseDown = false;
let isWasdEnabled = false;
let moveForward = false;
let moveBackward = false;
let moveLeft = false;
let moveRight = false;
let previousMouseEvent = {};
let shouldCheckInputSources = false;
const CHECK_INTERVAL = 2000;
let touchStarted = false;
let touchStart = {};
let isVr = false;
let hasLocomotion = false;
let speed = 0;
const pointDistanceThreshold = 0.1;
let pointedAtomR = null;
let pointedAtomL = null;

const euler = new THREE.Euler(0, 0, 0, "YXZ");

const limitBox = new THREE.Box3();
limitBox.set(new THREE.Vector3(-5, -5, -5), new THREE.Vector3(5, 5, 5));

const velocity = new THREE.Vector3();
const direction = new THREE.Vector3();

const canvas = document.querySelector("canvas.webgl");

const dummyObj = new THREE.Object3D();

// Camera Rig
let cameraRig = new THREE.Group();

const sizes = {
  width: window.innerWidth,
  height: window.innerHeight,
};

export const setup3DScene = () => {
  scene = new THREE.Scene();
  scene.background = new THREE.Color("skyblue");

  camera = new THREE.PerspectiveCamera(90, window.innerWidth / window.innerHeight, 0.1, 100);
  camera.position.set(0, 1.6, 3);
  controls = new OrbitControls(camera, canvas);
  controls.target.set(0, 1.6, 0);
  controls.autoRotate = true;
  controls.autoRotateSpeed = 0.2;
  controls.enableRotate = false;
  controls.enablePan = false;
  controls.enableZoom = false;
  controls.update();

  const listener = new THREE.AudioListener();
  camera.add(listener);

  sound = new THREE.Audio(listener);

  const audioLoader = new THREE.AudioLoader();
  audioLoader.load("https://killcloud.nyc3.cdn.digitaloceanspaces.com/assets/click.wav", function (buffer) {
    sound.setBuffer(buffer);
    sound.setLoop(false);
    sound.setVolume(1.0);
  });

  wasdControls = new PointerLockControls(camera, canvas);

  document.addEventListener("keydown", onKeyDown);
  document.addEventListener("keyup", onKeyUp);

  const testGeometry = new THREE.CircleGeometry(0.25, 16);
  const testMaterial2 = new THREE.MeshBasicMaterial({
    color: "black",
    wireframe: true,
  });
  const center = new THREE.Mesh(testGeometry, testMaterial2);
  center.rotateX(-Math.PI / 2);
  center.position.set(0, 0.01, 0);
  scene.add(center);

  // Lights
  const hemisphereLight = new THREE.HemisphereLight(0x606060, 0x404040, 10);
  scene.add(hemisphereLight);

  const light = new THREE.DirectionalLight(0xffffff, 5);
  light.position.set(0, 6, 0);
  scene.add(light);

  renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
  });
  renderer.setSize(sizes.width, sizes.height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.xr.enabled = true;
  // renderer.xr.cameraAutoUpdate = false;

  renderer.xr.addEventListener("sessionstart", handleSessionStart);
  renderer.xr.addEventListener("sessionend", handleSessionEnd);

  // Controllers
  controller1 = renderer.xr.getController(0);
  controller1.addEventListener("connected", onControllerConnected);
  controller1.addEventListener("disconnected", onControllerDisconnected);
  scene.add(controller1);

  controller2 = renderer.xr.getController(1);
  controller2.addEventListener("connected", onControllerConnected);
  controller2.addEventListener("disconnected", onControllerDisconnected);
  scene.add(controller2);

  const controllerModelFactory = new XRControllerModelFactory();

  // Hand1
  controllerGrip1 = renderer.xr.getControllerGrip(0);
  controllerGrip1.add(controllerModelFactory.createControllerModel(controllerGrip1));
  controllerGrip1.addEventListener("selectstart", onSelectStart);
  controllerGrip1.addEventListener("selectend", onSelectEnd);
  scene.add(controllerGrip1);
  controllerGrip1.add(cursorGroup.clone());

  hand1 = renderer.xr.getHand(0);
  hand1.add(new OculusHandModel(hand1));
  hand1.addEventListener("pinchstart", onPinchStart);
  hand1.addEventListener("pinchend", onPinchEnd);
  scene.add(hand1);

  // Hand2
  controllerGrip2 = renderer.xr.getControllerGrip(1);
  controllerGrip2.add(controllerModelFactory.createControllerModel(controllerGrip2));
  controllerGrip2.addEventListener("selectstart", onSelectStart);
  controllerGrip2.addEventListener("selectend", onSelectEnd);
  scene.add(controllerGrip2);
  controllerGrip2.add(cursorGroup.clone());

  hand2 = renderer.xr.getHand(1);
  hand2.add(new OculusHandModel(hand2));
  hand2.addEventListener("pinchstart", onPinchStart);
  hand2.addEventListener("pinchend", onPinchEnd);
  scene.add(hand2);

  environment = new THREE.Object3D();
  scene.add(environment);

  loader.load(
    // "https://cdn.glitch.global/56c73222-59c9-4ea8-ac67-46d18ffcc37e/model2.glb",
    // "https://killcloud.nyc3.cdn.digitaloceanspaces.com/scenes/ConferenceCenter.glb",
    "https://killcloud.nyc3.cdn.digitaloceanspaces.com/scenes/conf_optimized3.glb",
    function (glb) {
      glb.scene.scale.set(1, 1, 1);
      glb.scene.position.set(-11, 0, 1);
      const gallery = glb.scene;
      environment.add(gallery);
      limitBox.setFromObject(gallery);
    }
  );

  const epflLogo = textureLoader.load("images/epfl.jpg");
  const epflGeom = new THREE.PlaneGeometry(2.5, 1.1);
  const epflMat = new THREE.MeshBasicMaterial({ map: epflLogo });
  const epfl = new THREE.Mesh(epflGeom, epflMat);
  epfl.rotateY(-Math.PI / 2);
  epfl.position.set(4.85, 2.5, -1.7);
  environment.add(epfl);

  const lbmLogo = textureLoader.load("https://killcloud.nyc3.cdn.digitaloceanspaces.com/scenes/slides/wss.png"); //"images/lbm.png");
  const lbmGeom = new THREE.PlaneGeometry(1.79, 0.7);
  const lbmMat = new THREE.MeshBasicMaterial({ map: lbmLogo });
  const lbm = new THREE.Mesh(lbmGeom, lbmMat);
  lbm.rotateY(-Math.PI / 2);
  lbm.position.set(4.85, 2.5, 1.7);
  //environment.add(lbm);                                              //*** COMMENTED ALL FUNDER PNGS

  const snfLogo = textureLoader.load("images/snf.jpg");
  const snfGeom = new THREE.PlaneGeometry(3, 0.68);
  const snfMat = new THREE.MeshBasicMaterial({ map: snfLogo });
  const snf = new THREE.Mesh(snfGeom, snfMat);
  snf.rotateY(-Math.PI / 2);
  snf.position.set(4.85, 1.3, -1.7);
  //environment.add(snf);

  const haslerLogo = textureLoader.load("images/hasler.jpg");
  const haslerGeom = new THREE.PlaneGeometry(3, 0.68);
  const haslerMat = new THREE.MeshBasicMaterial({ map: haslerLogo });
  const hasler = new THREE.Mesh(haslerGeom, haslerMat);
  hasler.rotateY(-Math.PI / 2);
  hasler.position.set(4.85, 1.3, 1.7);
  // environment.add(hasler);

  const slideImg = textureLoader.load("https://killcloud.nyc3.cdn.digitaloceanspaces.com/scenes/slides/empty.jpeg");
  slideImg.colorSpace = THREE.SRGBColorSpace;
  const slideGeom = new THREE.PlaneGeometry(3.84, 2.16);
  slideMat = new THREE.MeshBasicMaterial({ map: slideImg });
  const slide = new THREE.Mesh(slideGeom, slideMat);
  slide.rotateY(Math.PI);
  slide.position.set(0, 2, 4.85);
  environment.add(slide);

  environment.rotateY(Math.PI);

  /*** 3D Toast ***/
  const uiBgStyles = {
    flexDirection: "row",
    gap: 0,
    width: "100%",
    borderRadius: 2,
    padding: 1,
    alignItems: "center",
    backgroundColor: 0x16141f,
    backgroundOpacity: 0.9,
  };

  const textContainerStyles = {
    padding: 2,
    height: "100%",
    width: "100%",
    justifyContent: "flex-start",
    flexDirection: "column",
    gap: 2,
  };

  toastAnchor = new THREE.Group();
  toastAnchor.position.set(0, 1.5, -0.6);
  scene.add(toastAnchor);
  toastRoot = new Root(camera, renderer, uiBgStyles);
  toastAnchor.add(toastRoot);

  toastMsg = new Text("My fancy toast message", { fontSize: 3, color: "white" });
  const toastMsgContainer = new Container(textContainerStyles);
  toastRoot.add(toastMsgContainer);
  toastMsgContainer.add(toastMsg);
  toastAnchor.visible = false;

  /*** 3D Labels ***/
  label1Anchor = new THREE.Group();
  label1Anchor.position.set(0, 1.5, -0.6);
  scene.add(label1Anchor);
  label1Root = new Root(camera, renderer, {
    flexDirection: "row",
    gap: 0,
    width: "100%",
    borderRadius: 1,
    padding: 0,
    alignItems: "center",
    backgroundColor: 0x16141f,
    backgroundOpacity: 0.8,
  });
  label1Anchor.add(label1Root);

  label1Msg = new Text("My label msg 1", { fontSize: 0.7, color: "white" });
  const label1MsgContainer = new Container({
    padding: 1,
    height: "100%",
    width: "100%",
    justifyContent: "flex-start", ///*********POR ACA */
    flexDirection: "column",
    gap: 2,
  });
  label1Root.add(label1MsgContainer);
  label1MsgContainer.add(label1Msg);
  label1Anchor.visible = false;

  label2Anchor = new THREE.Group();
  label2Anchor.position.set(0, 1.5, -0.6);
  scene.add(label2Anchor);
  label2Root = new Root(camera, renderer, {
    flexDirection: "row",
    gap: 0,
    width: "100%",
    borderRadius: 1,
    padding: 0,
    alignItems: "center",
    backgroundColor: 0x16141f,
    backgroundOpacity: 0.8,               //CHANGED
  });
  label2Anchor.add(label2Root);

  label2Msg = new Text("My label msg 2", { fontSize: 0.7, color: "white" });
  const label2MsgContainer = new Container({
    padding: 1,
    height: "100%",
    width: "100%",
    justifyContent: "flex-start",
    flexDirection: "column",
    gap: 2,
  });
  label2Root.add(label2MsgContainer);
  label2MsgContainer.add(label2Msg);
  label2Anchor.visible = false;

  /*** 3D Menu ***/
  menuAnchor = new THREE.Group();
  menuAnchor.position.set(-0.2, 1.3, -0.2);
  scene.add(menuAnchor);

  menuRoot = new Root(camera, renderer, {
    flexDirection: "column",
    gap: 0,
    width: 19,
    borderRadius: 2,
    padding: 0.8,
    alignItems: "center",
    backgroundColor: 0x16141f,
    backgroundOpacity: 0.8,
  });
  menuAnchor.add(menuRoot);
  menuAnchor.visible = false;

  const menuText = new Text("Settings", {
    fontSize: 1.5,
    textAlign: "left",
    color: "white",
    marginBottom: 0.5,
    fontWeight: "bold",
  });
  const tempText = new Text("Simulation", {
    fontSize: 1.1,
    textAlign: "left",
    color: "white",
    width: "100%",
    fontWeight: "bold",
  });
  const scaleText = new Text("Scale", {
    fontSize: 1.1,
    textAlign: "left",
    color: "white",
    width: "100%",
    marginTop: 1.25,
    fontWeight: "bold",
  });

  const othersText = new Text("More controls", {
    fontSize: 1.1,
    textAlign: "left",
    color: "white",
    width: "100%",
    marginTop: 1.25,
    fontWeight: "bold",
  });

  const buttonContainerStyles = {
    marginTop: 0.5,
    height: "100%",
    width: "100%",
    justifyContent: "space-between",
    flexDirection: "row",
    gap: 1,
  };

  const buttonStyles = {
    padding: 0.6,
    height: "100%",
    width: "100%",
    backgroundColor: 0x5c45eb,
    borderRadius: 1,
    fontSize: 1,
    color: "white",
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "bold",
    onClick: (e) => {
      e.object.setStyle({ backgroundColor: "white", color: 0x5c45eb });
      setTimeout(() => {
        e.object.setStyle({ backgroundColor: 0x5c45eb, color: "white" });
      }, 300);
    },
  };

  const buttonContainer1 = new Container(buttonContainerStyles); //MD (T+, T-, play/stop)
  const buttonContainer2 = new Container(buttonContainerStyles); //Scale
  const buttonContainer5 = new Container(buttonContainerStyles); //Others (WritePDB, ...)

  buttonIncreaseT = new Text("T+", buttonStyles);
  buttonDecreaseT = new Text("T-", buttonStyles);
  buttonPlayMD = new Text("Play", buttonStyles);
  buttonStopMD = new Text("Stop", buttonStyles);

  buttonZoomIn = new Text("Scale up", buttonStyles);
  buttonZoomOut = new Text("Scale down", buttonStyles);

  buttonWritePDB = new Text("Write PDB", buttonStyles);

  buttonClose = new Text("Close", {
    marginTop: 1,
    paddingTop: 0.6,
    paddingBottom: 0.6,
    paddingLeft: 1.2,
    paddingRight: 1.2,
    backgroundColor: 0x464356,
    borderRadius: 1,
    fontSize: 1,
    fontWeight: "bold",
    color: "white",
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
    onClick: (e) => {
      e.object.setStyle({ backgroundColor: "white", color: 0x464356 });
      setTimeout(() => {
        e.object.setStyle({ backgroundColor: 0x464356, color: "white" });
      }, 300);
    },
  });

  buttonContainer1.add(buttonIncreaseT, buttonDecreaseT, buttonPlayMD, buttonStopMD);
  buttonContainer2.add(buttonZoomIn, buttonZoomOut);
  buttonContainer5.add(buttonWritePDB);

  menuRoot.add(menuText);
  menuRoot.add(tempText);
  menuRoot.add(buttonContainer1);
  menuRoot.add(scaleText);
  menuRoot.add(buttonContainer2);
  menuRoot.add(othersText);
  menuRoot.add(buttonContainer5);
  menuRoot.add(buttonClose);

  /*** Wrist Button ***/
  wristButtonAnchor = new THREE.Group();

  const circleGeometry = new THREE.CircleGeometry(0.009, 32);
  const circleMaterial = new THREE.MeshBasicMaterial({
    color: 0x9bf99f,
  });
  wristButton = new THREE.Mesh(circleGeometry, circleMaterial);

  const outlineGeometry = new THREE.RingGeometry(0.004, 0.006, 64);

  const ringMaterial = new THREE.MeshBasicMaterial({
    color: 0x5c45eb,
  });

  const shapeOutline = new THREE.Mesh(outlineGeometry, ringMaterial);

  wristButton.add(shapeOutline);
  shapeOutline.position.z = 0.0001;
  wristButtonAnchor.add(wristButton);

  buttons = [
    {
      name: "TEMP+",
      button: buttonIncreaseT,
      isPressed: false,
      action: () => store.dispatch(sceneSlice.actions.emitTemperatureChange({ deltaT: 25 })),
    },
    {
      name: "TEMP-",
      button: buttonDecreaseT,
      isPressed: false,
      action: () => store.dispatch(sceneSlice.actions.emitTemperatureChange({ deltaT: -25 })),
    },
    {
      name: "PlayMD",
      button: buttonPlayMD,
      isPressed: false,
      action: () => store.dispatch(sessionSlice.actions.emitStartSim()),
    },
    {
      name: "StopMD",
      button: buttonStopMD,
      isPressed: false,
      action: () => store.dispatch(sessionSlice.actions.emitStopSim()),
    },
    {
      name: "WritePDB",
      button: buttonWritePDB,
      isPressed: false,
      action: () => store.dispatch(sessionSlice.actions.emitWritePDB()),
    },
    {
      name: "WRIST",
      button: wristButton,
      isPressed: false,
      action: () => display3DMenu(),
    },
    {
      name: "CLOSE_MENU",
      button: buttonClose,
      isPressed: false,
      action: () => close3dMenu(),
    },
    {
      name: "ZOOM+",
      button: buttonZoomIn,
      isPressed: false,
      action: () => store.dispatch(sceneSlice.actions.emitMoleculeScaleUpdate({ type: "up" })),
    },
    {
      name: "ZOOM-",
      button: buttonZoomOut,
      isPressed: false,
      action: () => store.dispatch(sceneSlice.actions.emitMoleculeScaleUpdate({ type: "down" })),
    },
  ];

  window.addEventListener("resize", () => {
    // Update sizes
    sizes.width = window.innerWidth;
    sizes.height = window.innerHeight;

    // Update camera
    camera.aspect = sizes.width / sizes.height;
    camera.updateProjectionMatrix();

    // Update renderer
    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  });

  const state = store.getState();
  const sessionType = selectSessionType(state);
  if (sessionType === "ATOMS") {
    gimbal = new THREE.Group();
    gimbal.name = 'gimbal';
    gimbal.position.set(-0.3, 1.3, -0.3); 

    const axisLength = 0.04;
    const axisWidth = 0.003;

    // X axis (red)
    const xGeometry = new THREE.CylinderGeometry(axisWidth, axisWidth, axisLength, 16);
    const xMaterial = new THREE.MeshBasicMaterial({ color: 0xff3b3b });
    const xAxis = new THREE.Mesh(xGeometry, xMaterial);
    xAxis.rotation.z = Math.PI / 2;
    xAxis.position.x = axisLength / 2;

    // Y axis (green)
    const yGeometry = new THREE.CylinderGeometry(axisWidth, axisWidth, axisLength, 16);
    const yMaterial = new THREE.MeshBasicMaterial({ color: 0x4bff4b });
    const yAxis = new THREE.Mesh(yGeometry, yMaterial);
    yAxis.position.y = axisLength / 2;

    // Z axis (blue)
    const zGeometry = new THREE.CylinderGeometry(axisWidth, axisWidth, axisLength, 16);
    const zMaterial = new THREE.MeshBasicMaterial({ color: 0x4b9fff });
    const zAxis = new THREE.Mesh(zGeometry, zMaterial);
    zAxis.rotation.x = Math.PI / 2;
    zAxis.position.z = axisLength / 2;

    gimbal.add(xAxis, yAxis, zAxis);
    scene.add(gimbal);
  }

  animate();
};

function animate() {
  renderer.setAnimationLoop(render);
}

/* ****** Render Loop  ****** */
function render(time) {
  const state = store.getState();
  const isConnected = selectIsConnected(state);
  const isInTrial = selectisInTrialMode(state);
  const isTyping = selectisTyping(state);
  const scaling = selectScaling(state);
  const sessionType = selectSessionType(state);
  const isInVR = selectisInVR(state);
  const session = renderer.xr.getSession();

  if (scaling.active) {
    let posReference1;
    let posReference2;

    if (isRHand) {
      posReference1 = hand1.joints["index-finger-tip"].position;
    } else {
      posReference1 = controllerGrip1.position;
    }

    if (isLHand) {
      posReference2 = hand2.joints["index-finger-tip"].position;
    } else {
      posReference2 = controllerGrip2.position;
    }

    const distance = posReference1.distanceTo(posReference2);
    scaling.objects.forEach((object) => {
      const newScale = (object.initialScale * distance) / object.initialDistance;
      const scalingEl = getElementById(object.objectId);
      scalingEl.mesh.scale.setScalar(newScale);
    });
  }

  if (shouldCheckInputSources) {
    const inputSourcesInfo = formatInputSources(session);
    handleControllerSetup(inputSourcesInfo);
    shouldCheckInputSources = false;
    controlEventCounter = 0;
  }

  if (counter > 4) {
    if (isConnected && isLeftControllerConnected && isRightControllerConnected && !isInTrial) {
      postControllers();
    }

    counter = 0;
  }

  counter++;

  if (sessionType !== "ATOMS") {
    if (counter2 > 4 && !isInTrial) {
      grabbedMeshes.forEach((grabbedMesh) => {
        postElement(grabbedMesh.id);
      });
      counter2 = 0;
    }
  }

  // Gimbal position broadcasting for ATOMS sessions
  if (sessionType === "ATOMS" && isGimbalGrabbed && counter2 > 4) {
    postGimbal();
    counter2 = 0;
  }

  counter2++;

  const currentTime = performance.now();

  if (controls.enabled) {
    controls.update();
  }

  if (hasLocomotion) {
    const input = Array.from(session.inputSources).find((source) => source.handedness === "left");

    if (input) {
      const delta = (currentTime - lastTime) / 1000;
      const deadzone = 0.1;
      const joystickX = Math.abs(input.gamepad.axes[2]) > deadzone ? -input.gamepad.axes[2] : 0;
      const joystickY = Math.abs(input.gamepad.axes[3]) > deadzone ? -input.gamepad.axes[3] : 0;
      speed = 2.5 * Math.sqrt(joystickX * joystickX + joystickY * joystickY);

      if (tunnelVignette) {
        const deploymentFactor = Math.min(1, speed / 0.9);
        tunnelVignette.position.z = (1 - deploymentFactor) * 0.4 - 0.4 / 2;
      }

      const cameraDirection = new THREE.Vector3();
      camera.getWorldDirection(cameraDirection);
      const moveVector = new THREE.Vector3(joystickX, 0, joystickY);
      const cameraYaw = Math.atan2(cameraDirection.x, cameraDirection.z);
      moveVector.applyAxisAngle(new THREE.Vector3(0, 1, 0), cameraYaw);

      if (moveVector.x !== 0) {
        cameraRig.position.x += moveVector.x * speed * delta;
      }
      if (moveVector.z !== 0) {
        cameraRig.position.z += moveVector.z * speed * delta;
      }

      if (cameraRig.position.x > limitBox.max.x - 0.3) {
        cameraRig.position.x = limitBox.max.x - 0.3;
      }

      if (cameraRig.position.x < limitBox.min.x + 0.3) {
        cameraRig.position.x = limitBox.min.x + 0.3;
      }

      if (cameraRig.position.z > limitBox.max.z - 0.7) {
        cameraRig.position.z = limitBox.max.z - 0.7;
      }

      if (cameraRig.position.z < limitBox.min.z + 0.7) {
        cameraRig.position.z = limitBox.min.z + 0.7;
      }
    }
  }

  if (isWasdEnabled && !isTyping) {
    const delta = (currentTime - lastTime) / 1000;

    velocity.x -= velocity.x * 10.0 * delta;
    velocity.z -= velocity.z * 10.0 * delta;

    velocity.y -= 9.8 * 100.0 * delta; // 100.0 = mass

    direction.z = Number(moveForward) - Number(moveBackward);
    direction.x = Number(moveRight) - Number(moveLeft);
    direction.normalize(); // this ensures consistent movements in all directions

    if (moveForward || moveBackward) velocity.z -= direction.z * speed * delta;
    if (moveLeft || moveRight) velocity.x -= direction.x * speed * delta;

    wasdControls.moveRight(-velocity.x * delta);
    wasdControls.moveForward(-velocity.z * delta);

    wasdControls.getObject().position.y += velocity.y * delta; // new behavior

    if (wasdControls.getObject().position.y < 10) {
      velocity.y = 0;
      wasdControls.getObject().position.y = 1.6;
    }

    if (camera.position.x > limitBox.max.x - 0.3) {
      camera.position.x = limitBox.max.x - 0.3;
    }

    if (camera.position.x < limitBox.min.x + 0.3) {
      camera.position.x = limitBox.min.x + 0.3;
    }

    if (camera.position.z > limitBox.max.z - 0.7) {
      camera.position.z = limitBox.max.z - 0.7;
    }

    if (camera.position.z < limitBox.min.z + 0.7) {
      camera.position.z = limitBox.min.z + 0.7;
    }
  }

  frameCounter++;
  if (
    frameCounter % 3 === 0 &&
    isConnected &&
    isLeftControllerConnected &&
    isRightControllerConnected &&
    sessionType === "ATOMS"
  ) {
    // Check if user is pointing to an atom
    checkPointing();
    update3DLabels();

    // Check if user is clicking a button
    checkButtonsInteraction();
  }
  updateRubbers();
  updateUi(time);

  lastTime = currentTime;
  debugCounter++;

  renderer.render(scene, camera);
}

/************** Events  **************/
function onPinchEnd(e) {
  const state = store.getState();
  const isInTrial = selectisInTrialMode(state);
  const scaling = selectScaling(state);
  const sessionType = selectSessionType(state);
  const controller = e.target;

  if (sessionType === "ATOMS") {
    // Check if releasing gimbal
    if (isGimbalGrabbed && gimbalGrabbingController === controller) {
      isGimbalGrabbed = false;
      gimbalGrabbingController = null;
      scene.attach(gimbal);

      // Clean up visual feedback sphere
      if (gimbalGrabSphere) {
        scene.attach(gimbalGrabSphere);
        gimbalGrabSphere.geometry.dispose();
        gimbalGrabSphere.material.dispose();
        scene.remove(gimbalGrabSphere);
        gimbalGrabSphere = null;
      }

      store.dispatch(sceneSlice.actions.emitGimbalUngrabbed());
      return;
    }

    const isSimOn = selectSimState(state);
    if (isSimOn) {
      const controllerName = controller === hand1 ? "hand1" : "hand2";
      store.dispatch(sceneSlice.actions.emitRemotePinchEnd(controllerName));
    }
    return;
  }

  if (controller.userData.selected !== undefined) {
    /* multiple collide */
    const { objects, sphere } = controller.userData.selected;

    const id = controller.id;
    const index = grabbingControllers.indexOf(id);
    grabbingControllers.splice(index, 1);

    scene.attach(sphere);
    sphere.geometry.dispose();
    sphere.material.dispose();
    scene.remove(sphere);

    controller.userData.selected = undefined;

    if (!scaling.active) {
      objects.forEach((object) => {
        const objectId = object.id;
        const index2 = grabbedMeshes.findIndex((grabbedMesh) => grabbedMesh.id === objectId);
        grabbedMeshes.splice(index2, 1);
        scene.attach(object.mesh);
        if (!isInTrial) {
          store.dispatch(
            sceneSlice.actions.emitElementUngrabbed({
              element: object.id,
            })
          );
        }
      });
    } else {
      store.dispatch(sceneSlice.actions.stopScaling());
    }
  }
}

function onPinchStart(e) {
  const state = store.getState();
  const isInTrial = selectisInTrialMode(state);
  const canIGrab = selectGrabbingPermission(state) || isInTrial;
  const sessionType = selectSessionType(state);
  const isMinimizing = selectisMinimizing(state);

  if (initialInputSource === "controller") {
    return;
  }

  if (
    !(isRHand && isLHand) ||
    (!isLeftControllerConnected && !isRightControllerConnected) ||
    !canIGrab ||
    isMinimizing
  ) {
    return;
  }

  const controller = e.target;
  const id = controller.id;
  const wrist = controller.joints["wrist"];
  const indexTip = controller.joints["index-finger-tip"];
  const otherIndexTip = controller === hand1 ? hand2 : hand1;

  if (sessionType === "ATOMS") {
    // Check gimbal collision BEFORE molecule logic
    if (collideGimbal(indexTip) && !gimbalOwner) {
      isGimbalGrabbed = true;
      gimbalGrabbingController = controller;
      wrist.attach(gimbal);

      // Visual feedback sphere
      gimbalGrabSphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
      gimbalGrabSphere.scale.set(0.02, 0.02, 0.02);
      indexTip.attach(gimbalGrabSphere);
      gimbalGrabSphere.position.set(0, 0, 0);

      store.dispatch(sceneSlice.actions.emitGimbalGrabbed());
      return;
    }

    const isSimOn = selectSimState(state);
    if (isSimOn) {
      const controllerName = controller === hand1 ? "hand1" : "hand2";
      const handedness = controller.children[0].xrInputSource.handedness;
      store.dispatch(sceneSlice.actions.emitRemotePinchStart({ controllerName, handedness }));
    }

    return;
  }

  const objects = collideMultipleObjects(indexTip);

  /* multiple collide*/

  let amIGrabbingThis = false;

  for (let i = 0; i < objects.length; i++) {
    amIGrabbingThis = grabbedMeshes.some((grabbedMesh) => grabbedMesh.id === objects[i].id);
    if (amIGrabbingThis) {
      break;
    }
  }

  if (objects.length > 0 && amIGrabbingThis) {
    grabbingControllers.push(id);
    let scalingInfo = [];

    objects.forEach((object) => {
      const info = {
        objectId: object.id,
        initialScale: object.mesh.scale.x,
        initialDistance: indexTip.position.distanceTo(otherIndexTip.joints["index-finger-tip"].position),
      };
      scalingInfo.push(info);
    });

    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    sphere.scale.set(0.05, 0.05, 0.05);
    indexTip.attach(sphere);
    sphere.position.set(0, 0, 0);

    controller.userData.selected = { objects, sphere };
    store.dispatch(sceneSlice.actions.startScaling(scalingInfo));
  }

  if (objects.length > 0 && !amIGrabbingThis) {
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    sphere.scale.set(0.05, 0.05, 0.05);
    grabbingControllers.push(id);
    objects.forEach((object) => {
      grabbedMeshes.push({ id: object.id });
      wrist.attach(object.mesh);

      if (!isInTrial) {
        store.dispatch(
          sceneSlice.actions.emitElementGrabbed({
            element: object.id,
          })
        );
      }
    });
    controller.userData.selected = { objects, sphere };
    indexTip.attach(sphere);
    sphere.position.set(0, 0, 0);
  }
}

function onSelectEnd(e) {
  if (isRHand) {
    return;
  }

  const state = store.getState();
  const isInTrial = selectisInTrialMode(state);
  const scaling = selectScaling(state);
  const sessionType = selectSessionType(state);
  const controller = e.target;

  if (sessionType === "ATOMS") {
    // Check if releasing gimbal
    if (isGimbalGrabbed && gimbalGrabbingController === controller) {
      isGimbalGrabbed = false;
      gimbalGrabbingController = null;
      scene.attach(gimbal);

      // Clean up visual feedback sphere
      if (gimbalGrabSphere) {
        scene.attach(gimbalGrabSphere);
        gimbalGrabSphere.geometry.dispose();
        gimbalGrabSphere.material.dispose();
        scene.remove(gimbalGrabSphere);
        gimbalGrabSphere = null;
      }

      store.dispatch(sceneSlice.actions.emitGimbalUngrabbed());
      return;
    }

    const controllerName = controller === controllerGrip1 ? "controller1" : "controller2";
    store.dispatch(sceneSlice.actions.emitRemotePinchEnd(controllerName));
    return;
  }

  if (controller.userData.selected !== undefined) {
    /* multiple collide */
    const { objects, sphere } = controller.userData.selected;

    const id = controller.id;
    const index = grabbingControllers.indexOf(id);
    grabbingControllers.splice(index, 1);

    scene.attach(sphere);
    sphere.geometry.dispose();
    sphere.material.dispose();
    scene.remove(sphere);

    controller.userData.selected = undefined;

    if (!scaling.active) {
      objects.forEach((object) => {
        const objectId = object.id;
        const index2 = grabbedMeshes.findIndex((grabbedMesh) => grabbedMesh.id === objectId);
        grabbedMeshes.splice(index2, 1);
        scene.attach(object.mesh);
        if (!isInTrial) {
          store.dispatch(
            sceneSlice.actions.emitElementUngrabbed({
              element: object.id,
            })
          );
        }
      });
    } else {
      store.dispatch(sceneSlice.actions.stopScaling());
    }
  }
}

function onSelectStart(e) {
  const state = store.getState();
  const isInTrial = selectisInTrialMode(state);
  const canIGrab = selectGrabbingPermission(state) || isInTrial;
  const sessionType = selectSessionType(state);
  const isMinimizing = selectisMinimizing(state);

  if (initialInputSource === "hand") {
    return;
  }

  if (isRHand || isLHand || (!isLeftControllerConnected && !isRightControllerConnected) || !canIGrab || isMinimizing) {
    return;
  }

  const controller = e.target;
  const id = controller.id;
  const otherController = controller === controllerGrip1 ? controllerGrip2 : controllerGrip1;
  const pivot = this.getObjectByName("pivot");

  if (sessionType === "ATOMS") {
    // Check gimbal collision BEFORE molecule logic
    if (collideGimbal(pivot) && !gimbalOwner) {
      isGimbalGrabbed = true;
      gimbalGrabbingController = controller;
      pivot.attach(gimbal);

      // Visual feedback sphere
      gimbalGrabSphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
      gimbalGrabSphere.scale.set(0.02, 0.02, 0.02);
      pivot.attach(gimbalGrabSphere);
      gimbalGrabSphere.position.set(0, 0, 0);

      store.dispatch(sceneSlice.actions.emitGimbalGrabbed());
      return;
    }

    const isSimOn = selectSimState(state);
    if (isSimOn) {
      const controllerName = controller === controllerGrip1 ? "controller1" : "controller2";
      const handedness = controller.children[0].motionController.xrInputSource.handedness;
      store.dispatch(sceneSlice.actions.emitRemotePinchStart({ controllerName, handedness }));
    }
    return;
  }

  const objects = collideMultipleObjects(pivot);

  /* multiple collide*/

  let amIGrabbingThis = false;

  for (let i = 0; i < objects.length; i++) {
    amIGrabbingThis = grabbedMeshes.some((grabbedMesh) => grabbedMesh.id === objects[i].id);
    if (amIGrabbingThis) {
      break;
    }
  }

  if (objects.length > 0 && amIGrabbingThis) {
    grabbingControllers.push(id);
    let scalingInfo = [];

    objects.forEach((object) => {
      const info = {
        objectId: object.id,
        initialScale: object.mesh.scale.x,
        initialDistance: controller.position.distanceTo(otherController.position),
      };
      scalingInfo.push(info);
    });

    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    sphere.scale.set(0.05, 0.05, 0.05);
    pivot.attach(sphere);
    sphere.position.set(0, 0, 0);

    controller.userData.selected = { objects, sphere };
    store.dispatch(sceneSlice.actions.startScaling(scalingInfo));
  }

  if (objects.length > 0 && !amIGrabbingThis) {
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    sphere.scale.set(0.05, 0.05, 0.05);
    grabbingControllers.push(id);
    objects.forEach((object) => {
      grabbedMeshes.push({ id: object.id });
      pivot.attach(object.mesh);

      if (!isInTrial) {
        store.dispatch(
          sceneSlice.actions.emitElementGrabbed({
            element: object.id,
          })
        );
      }
    });
    controller.userData.selected = { objects, sphere };
    pivot.attach(sphere);
    sphere.position.set(0, 0, 0);
  }
}

function onControllerConnected(event) {
  controlEventCounter++;
  if (controlEventCounter === 2) {
    setTimeout(() => {
      shouldCheckInputSources = true;
    }, CHECK_INTERVAL);
  }
}

function onControllerDisconnected(e) {
  const state = store.getState();
  const sessionType = selectSessionType(state);
  const controller = e.target;

  if (sessionType === "ATOMS") {
    const isSimOn = selectSimState(state);
    if (isSimOn) {
      let controllerName;
      if (isRHand) {
        controllerName = controller === hand1 ? "hand1" : "hand2";
      } else {
        controllerName = controller === controllerGrip1 ? "controller1" : "controller2";
      }
      store.dispatch(sceneSlice.actions.emitRemotePinchEnd(controllerName));
    }
  }

  isColorUpdated = false;
}

function handleSessionStart(event) {
  controls.enabled = false;
  isWasdEnabled = false;
}

function handleSessionEnd(event) {
  isRightControllerConnected = false;
  isLeftControllerConnected = false;
  isWasdEnabled = true;
  environment.visible = true;
  isColorUpdated = false;
  isVr = false;
  menuAnchor.visible = false;
  controlEventCounter = 0;
  if (hasLocomotion) {
    hasLocomotion = false;
    cleanupVignette();
  }
  scene.attach(camera);
  scene.attach(controllerGrip1);
  scene.attach(controllerGrip2);
  store.dispatch(sceneSlice.actions.emitControllerDisconnect());
}

function isPointing(hand) {
  if (isRHand && isLHand && isLeftControllerConnected && isRightControllerConnected) {
    const middlePosition = hand.joints["middle-finger-tip"].position;
    const indexTipPosition = hand.joints["index-finger-tip"].position;

    const distance = middlePosition.distanceTo(indexTipPosition);

    if (distance > pointDistanceThreshold) {
      return true;
    }
  }

  return false;
}

function checkHandButton() {
  if (!hand1?.joints?.["index-finger-tip"] && !hand2?.joints?.["index-finger-tip"]) return;

  const fingerTip1 = new THREE.Vector3();
  const fingerTip2 = new THREE.Vector3();
  hand1.joints["index-finger-tip"].getWorldPosition(fingerTip1);
  hand2.joints["index-finger-tip"].getWorldPosition(fingerTip2);

  buttons.forEach((button) => {
    if (!button) return;

    if (!menuAnchor.visible && button.name !== "WRIST") {
      return;
    }

    const buttonPosition = new THREE.Vector3();
    button.button.getWorldPosition(buttonPosition);
    const distance1 = fingerTip1.distanceTo(buttonPosition);
    const distance2 = fingerTip2.distanceTo(buttonPosition);

    const isTouching = distance1 < HAND_TRIGGER_DISTANCE || distance2 < HAND_TRIGGER_DISTANCE;

    if (isTouching) {
      // Finger is touching the button
      if (!button.isPressed) {
        button.isPressed = true;
        button.action();
        sound.play();
        // Dispatch click event
        button.button.dispatchEvent({
          type: "click",
          distance: Math.min(distance1, distance2),
          nativeEvent: {},
          object: button.button,
          point: new THREE.Vector3(),
          pointerId: -1,
        });
      }
    } else {
      button.isPressed = false;
    }
  });
}

/************** Functions  **************/
export function clearAtoms() {
  if (bonds) {
    scene.remove(bonds);
    bonds.dispose();
    bonds = null;
  }

  if (atoms) {
    scene.remove(atoms);
    atoms.dispose();
    atoms = null;
  }

  rubberMeshes.forEach((rubber) => {
    scene.remove(rubber.mesh);
    rubber.mesh.dispose();
  });
}
export function cleanUp() {
  if (bonds) {
    scene.remove(bonds);
    bonds.dispose();
    bonds = null;
  }

  if (atoms) {
    scene.remove(atoms);
    atoms.dispose();
    atoms = null;
  }

  rubberMeshes.forEach((rubber) => {
    scene.remove(rubber.mesh);
    rubber.mesh.dispose();
  });

  elementMeshes.forEach((element) => {
    clearGroup(element.mesh);
    scene.remove(element.mesh);
  });

  userMeshes.forEach((user) => {
    clearGroup(user.avatar);
    scene.remove(user.avatar);

    clearGroup(user.right.mesh);
    scene.remove(user.right.mesh);

    clearGroup(user.left.mesh);
    scene.remove(user.left.mesh);
  });

  if (scene) {
    scene.background = new THREE.Color("skyblue");
    environment.visible = true;
    controls.enableRotate = false;
    controls.enablePan = false;
    controls.enableZoom = false;
  }

  elementMeshes = [];
  userMeshes = [];
  rubberMeshes = [];

  // Reset gimbal state
  gimbal = null;
  isGimbalGrabbed = false;
  gimbalGrabbingController = null;
  gimbalOwner = null;
}

function collideObject(indexTip) {
  const tmpVector = new THREE.Vector3();
  const indexPos = indexTip.getWorldPosition(tmpVector);

  const state = store.getState();
  const elements = selectAvailableElements(state);

  let collideEl = null;

  for (let i = 0; i < elements.length; i++) {
    const myElement = getElementById(elements[i].id);
    const box = new THREE.Box3().setFromObject(myElement.mesh);
    if (box.containsPoint(indexPos)) {
      collideEl = myElement;
      break;
    }
  }

  return collideEl;
}

function collideMultipleObjects(indexTip) {
  const tmpVector = new THREE.Vector3();
  const indexPos = indexTip.getWorldPosition(tmpVector);

  const state = store.getState();
  const elements = selectAvailableElements(state);
  const canGrabMultipleObjs = selectMultipleGrabbingPermission(state);

  let collideElements = [];

  for (let i = 0; i < elements.length; i++) {
    const myElement = getElementById(elements[i].id);
    const box = new THREE.Box3().setFromObject(myElement.mesh);
    if (box.containsPoint(indexPos)) {
      collideElements.push(myElement);
      if (!canGrabMultipleObjs) {
        break;
      }
    }
  }

  return collideElements;
}

function collideGimbal(positionRef) {
  if (!gimbal) return false;

  const tmpVector = new THREE.Vector3();
  const pos = positionRef.getWorldPosition(tmpVector);

  // Create bounding box around gimbal with padding for easier grabbing
  const box = new THREE.Box3().setFromObject(gimbal);
  box.expandByScalar(0.02);

  return box.containsPoint(pos);
}

function handleControllerSetup(inputSources) {
  const state = store.getState();
  const isInTrial = selectisInTrialMode(state);

  let type;

  inputSources.forEach((info) => {
    if (info.handedness === "right") {
      isRightControllerConnected = true;
      isRHand = info.controllerType === "hand";
    } else {
      isLeftControllerConnected = true;
      isLHand = info.controllerType === "hand";
    }
    console.log(`Connected ${info.handedness} ${info.controllerType}`);
    type = info.controllerType;
  });

  hasLocomotion = type === "controller" && isVr;

  if (hasLocomotion) {
    //Add Camera and controllergrips to camera group
    scene.add(cameraRig);
    cameraRig.add(camera);
    cameraRig.add(controllerGrip1);
    cameraRig.add(controllerGrip2);
    tunnelVignette = createTunnelVignette();
    camera.add(tunnelVignette);
  }

  if (isInTrial) {
    return;
  }

  const data = {
    type,
  };

  if (initialInputSource === null) {
    initialInputSource = type;
  } else if (initialInputSource !== type) {
    console.log("Switching controllers - Please use the initial ones");
  }

  store.dispatch(sceneSlice.actions.emitControllerConnect(data));
}

export async function createUser(user) {
  const avatar = createAvatar(user.color);
  // avatar.position.set(user.x, 1.6, user.z);
  scene.add(avatar);
  if (user.type === "hand") {
    const { hand: leftHand, bones: leftBones } = await createHand("left", user.color);
    scene.add(leftHand);
    const { hand: rightHand, bones: rightBones } = await createHand("right", user.color);
    scene.add(rightHand);

    const userProfile = {
      id: user.id,
      type: user.type,
      avatar,
      left: {
        mesh: leftHand,
        bones: leftBones,
      },
      right: {
        mesh: rightHand,
        bones: rightBones,
      },
    };
    userMeshes.push({ ...userProfile });
  } else {
    const { controller: leftController } = await createController("left", user.color);
    scene.add(leftController);
    const { controller: rightController } = await createController("right", user.color);
    scene.add(rightController);

    const userProfile = {
      id: user.id,
      type: user.type,
      avatar,
      left: {
        mesh: leftController,
      },
      right: {
        mesh: rightController,
      },
    };
    userMeshes.push({ ...userProfile });
  }
}

export function updateUserVisibility(id, visibility) {
  const userIndex = userMeshes.findIndex((user) => user.id === id);

  if (userIndex >= 0) {
    userMeshes[userIndex].right.mesh.visible = visibility;
    userMeshes[userIndex].left.mesh.visible = visibility;
    userMeshes[userIndex].avatar.visible = visibility;
  }
}

export function updateElementScale(data) {
  const element = elementMeshes.find((element) => element.id === data.element.id);
  element.mesh.scale.set(data.element.scale, data.element.scale, data.element.scale);
}

export function updateElementTransform(data) {
  const element = elementMeshes.find((element) => element.id === data.elId);

  const position = new THREE.Vector3(data.position.x, data.position.y, data.position.z);

  const quaternion = new THREE.Quaternion(data.quaternion.x, data.quaternion.y, data.quaternion.z, data.quaternion.w);

  element.mesh.position.copy(position);
  element.mesh.setRotationFromQuaternion(quaternion);

  element.mesh.scale.set(data.scale, data.scale, data.scale);
}

export function updateGimbalTransform(data) {
  console.log("Am I ready? ", !gimbal || isGimbalGrabbed)
  if (!gimbal || isGimbalGrabbed) return;  // Don't update if we're grabbing it

  const position = new THREE.Vector3(data.position.x, data.position.y, data.position.z);
  const quaternion = new THREE.Quaternion(
    data.quaternion.x,
    data.quaternion.y,
    data.quaternion.z,
    data.quaternion.w
  );

  gimbal.position.copy(position);
  gimbal.setRotationFromQuaternion(quaternion);
}

export function setGimbalRotationByValue(rotations) {
  if (!gimbal || isGimbalGrabbed) return;
  const euler = new THREE.Euler(rotations.rotX, rotations.rotY, rotations.rotZ);
  const quaternion = new THREE.Quaternion().setFromEuler(euler);
  gimbal.setRotationFromQuaternion(quaternion);
}

export function changeGimbalRotation(rotations) {
  if (!gimbal || isGimbalGrabbed) return;
  const currentQuaternion = new THREE.Quaternion();
  gimbal.getWorldQuaternion(currentQuaternion);
  const currentEuler = new THREE.Euler().setFromQuaternion(currentQuaternion);
  const newEuler = new THREE.Euler(
    currentEuler.x + rotations.rotX,
    currentEuler.y + rotations.rotY,
    currentEuler.z + rotations.rotZ
  );
  gimbal.setRotationFromQuaternion(new THREE.Quaternion().setFromEuler(newEuler));
}

export function updateGimbalOwnership(data) {
  gimbalOwner = data.owner;
}

export function deleteElement(data) {
  const elIndex = elementMeshes.findIndex((element) => element.id === data.id);

  scene.remove(elementMeshes[elIndex].mesh);
  clearGroup(elementMeshes[elIndex].mesh);

  elementMeshes.splice(elIndex, 1);
}

export function recenterElement(data) {
  const element = elementMeshes.find((element) => element.id === data.id);
  element.owner = null;
  element.isGrabbed = false;
  element.mesh.position.set(0, 1.5, 0);
}

function postElement(id) {
  const grabbedEl = elementMeshes.find((element) => element.id === id);
  const quaternion = new THREE.Quaternion();
  const position = new THREE.Vector3();
  grabbedEl.mesh.getWorldQuaternion(quaternion);
  grabbedEl.mesh.getWorldPosition(position);

  const data = {
    id,
    quaternion: {
      x: quaternion._x,
      y: quaternion._y,
      z: quaternion._z,
      w: quaternion._w,
    },
    position: {
      x: position.x,
      y: position.y,
      z: position.z,
    },
    scale: grabbedEl.mesh.scale.x,
  };

  store.dispatch(sceneSlice.actions.emitElementUpdate(data));
}

function postGimbal() {
  if (!gimbal) return;

  const quaternion = new THREE.Quaternion();
  const position = new THREE.Vector3();
  gimbal.getWorldQuaternion(quaternion);
  gimbal.getWorldPosition(position);

  const euler = new THREE.Euler().setFromQuaternion(quaternion);
  const newMolRotations = {
    rotX: euler.x,
    rotY: euler.y,
    rotZ: euler.z,
  };
  
  const data = {
    quaternion: {
      x: quaternion._x,
      y: quaternion._y,
      z: quaternion._z,
      w: quaternion._w,
    },
    position: {
      x: position.x,
      y: position.y,
      z: position.z,
    },
    ownerId: selectMyId(store.getState()),
  };
  
  store.dispatch(sceneSlice.actions.emitGimbalUpdate(data));
  store.dispatch(sessionSlice.actions.emitSetMolRotationByValue({ newMolRotations }));
}

function postControllers() {
  const xrCamera = renderer.xr.getCamera();
  const camQuaternion = new THREE.Quaternion();
  const camPosition = new THREE.Vector3();
  camQuaternion.copy(xrCamera.quaternion);
  camPosition.copy(xrCamera.position);

  if (hasLocomotion) {
    camPosition.applyMatrix4(cameraRig.matrix);
  }

  if (isRHand && isLHand) {
    let hand1Data = {};
    let hand2Data = {};

    if (!isColorUpdated) {
      const state = store.getState();
      const color = selectColor(state);
      const sessionType = selectSessionType(state);
      const mesh1 = hand1.getObjectByProperty("type", "SkinnedMesh");
      const mesh2 = hand2.getObjectByProperty("type", "SkinnedMesh");

      const newMaterial = new THREE.MeshLambertMaterial({
        color,
      });

      if (mesh1 && mesh2) {
        mesh1.traverse(function (node) {
          if (node.isMesh) {
            node.material = newMaterial;
            node.material.needsUpdate = true;
          }
        });

        mesh2.traverse(function (node) {
          if (node.isMesh) {
            node.material = newMaterial;
            node.material.needsUpdate = true;
          }
        });
        isColorUpdated = true;
      }

      if (sessionType === "ATOMS") {
        if (hand1.children[0].xrInputSource.handedness === "left") {
          const wrist = hand1.joints["wrist"];
          wrist.attach(wristButtonAnchor);
        } else {
          const wrist = hand2.joints["wrist"];
          wrist.attach(wristButtonAnchor);
        }
        wristButtonAnchor.position.set(0, -0.02, 0.025);
        wristButtonAnchor.rotation.set(Math.PI / 2, 0, 0);
      }
    }

    for (let i = 0; i < joints.length; i++) {
      const joint = joints[i];
      hand1Data.handedness = hand1.children[0].xrInputSource.handedness;
      hand2Data.handedness = hand2.children[0].xrInputSource.handedness;
      if (hand1.joints[joint]) {
        const quaternion1 = new THREE.Quaternion();
        const position1 = new THREE.Vector3();
        hand1.joints[joint].getWorldQuaternion(quaternion1);
        hand1.joints[joint].getWorldPosition(position1);
        hand1Data[joint] = {
          position: { x: position1.x, y: position1.y, z: position1.z },
          quaternion: {
            x: quaternion1._x,
            y: quaternion1._y,
            z: quaternion1._z,
            w: quaternion1._w,
          },
          visible: hand1.joints[joint].visible,
        };
      }

      if (hand2.joints[joint]) {
        const quaternion2 = new THREE.Quaternion();
        const position2 = new THREE.Vector3();
        hand2.joints[joint].getWorldQuaternion(quaternion2);
        hand2.joints[joint].getWorldPosition(position2);
        hand2Data[joint] = {
          position: { x: position2.x, y: position2.y, z: position2.z },
          quaternion: {
            x: quaternion2._x,
            y: quaternion2._y,
            z: quaternion2._z,
            w: quaternion2._w,
          },
          visible: hand2.joints[joint].visible,
        };
      }
    }

    const data = {
      hand1: hand1Data,
      hand2: hand2Data,
      avatar: {
        position: { x: camPosition.x, y: camPosition.y, z: camPosition.z },
        quaternion: {
          x: camQuaternion._x,
          y: camQuaternion._y,
          z: camQuaternion._z,
          w: camQuaternion._w,
        },
      },
    };

    store.dispatch(sceneSlice.actions.emitControllerUpdate(data));
  } else {
    const quaternion1 = new THREE.Quaternion();
    const position1 = new THREE.Vector3();
    const pivotPosition1 = new THREE.Vector3();
    controllerGrip1.getWorldQuaternion(quaternion1);
    controllerGrip1.getWorldPosition(position1);
    const pivot1 = controllerGrip1.getObjectByName("pivot");
    pivot1.getWorldPosition(pivotPosition1);
    const handedness1 = controllerGrip1.children[0]?.motionController?.xrInputSource?.handedness;

    const quaternion2 = new THREE.Quaternion();
    const position2 = new THREE.Vector3();
    const pivotPosition2 = new THREE.Vector3();
    controllerGrip2.getWorldQuaternion(quaternion2);
    controllerGrip2.getWorldPosition(position2);
    const pivot2 = controllerGrip2.getObjectByName("pivot");
    pivot2.getWorldPosition(pivotPosition2);
    const handedness2 = controllerGrip2.children[0]?.motionController?.xrInputSource?.handedness;

    if (!handedness1 || !handedness2) {
      return;
    }

    const data = {
      controller1: {
        handedness: handedness1,
        position: { x: position1.x, y: position1.y, z: position1.z },
        quaternion: {
          x: quaternion1._x,
          y: quaternion1._y,
          z: quaternion1._z,
          w: quaternion1._w,
        },
        pivot: {
          x: pivotPosition1.x,
          y: pivotPosition1.y,
          z: pivotPosition1.z,
        },
      },
      controller2: {
        handedness: handedness2,
        position: { x: position2.x, y: position2.y, z: position2.z },
        quaternion: {
          x: quaternion2._x,
          y: quaternion2._y,
          z: quaternion2._z,
          w: quaternion2._w,
        },
        pivot: {
          x: pivotPosition2.x,
          y: pivotPosition2.y,
          z: pivotPosition2.z,
        },
      },
      avatar: {
        position: { x: camPosition.x, y: camPosition.y, z: camPosition.z },
        quaternion: {
          x: camQuaternion._x,
          y: camQuaternion._y,
          z: camQuaternion._z,
          w: camQuaternion._w,
        },
      },
    };

    store.dispatch(sceneSlice.actions.emitControllerUpdate(data));
  }
}

export function renderUserUpdate(data) {
  const user = userMeshes.find((user) => user.id === data.id);
  const controllerData = data.controllerData;
  const isUserCreated = user && user.left && user.right;

  if (isUserCreated) {
    const avatarPos = new THREE.Vector3(
      controllerData.avatar.position.x,
      controllerData.avatar.position.y,
      controllerData.avatar.position.z
    );

    const avatarQuat = new THREE.Quaternion(
      controllerData.avatar.quaternion.x,
      controllerData.avatar.quaternion.y,
      controllerData.avatar.quaternion.z,
      controllerData.avatar.quaternion.w
    );
    user.avatar.position.copy(avatarPos);
    user.avatar.quaternion.copy(avatarQuat);
  }

  // Received hand data - user was created with hands
  if (controllerData.hand1 && controllerData.hand2 && isUserCreated && user.type === "hand") {
    const leftHand = controllerData.hand1.handedness === "left" ? controllerData.hand1 : controllerData.hand2;
    const rightHand = controllerData.hand2.handedness === "right" ? controllerData.hand2 : controllerData.hand1;
    for (let i = 0; i < user.left.bones.length; i++) {
      const bone = user.left.bones[i];
      const joint = bone.jointName;

      if (bone && leftHand[joint]) {
        const position1 = new THREE.Vector3(
          leftHand[joint].position.x,
          leftHand[joint].position.y,
          leftHand[joint].position.z
        );

        const quaternion1 = new THREE.Quaternion(
          leftHand[joint].quaternion.x,
          leftHand[joint].quaternion.y,
          leftHand[joint].quaternion.z,
          leftHand[joint].quaternion.w
        );

        bone.position.copy(position1);
        bone.setRotationFromQuaternion(quaternion1);
      }
    }

    for (let i = 0; i < user.right.bones.length; i++) {
      const bone = user.right.bones[i];
      const joint = bone.jointName;

      if (bone && rightHand[joint]) {
        const position2 = new THREE.Vector3(
          rightHand[joint].position.x,
          rightHand[joint].position.y,
          rightHand[joint].position.z
        );

        const quaternion2 = new THREE.Quaternion(
          rightHand[joint].quaternion.x,
          rightHand[joint].quaternion.y,
          rightHand[joint].quaternion.z,
          rightHand[joint].quaternion.w
        );

        bone.position.copy(position2);
        bone.setRotationFromQuaternion(quaternion2);
      }
    }
  } else if (controllerData.controller1 && controllerData.controller2 && isUserCreated && user.type === "controller") {
    // Received controller data - user was created with controllers
    const leftController =
      controllerData.controller1.handedness === "left" ? controllerData.controller1 : controllerData.controller2;
    const rightController =
      controllerData.controller2.handedness === "right" ? controllerData.controller2 : controllerData.controller1;

    const position1 = new THREE.Vector3(
      leftController.position.x,
      leftController.position.y,
      leftController.position.z
    );

    const quaternion1 = new THREE.Quaternion(
      leftController.quaternion.x,
      leftController.quaternion.y,
      leftController.quaternion.z,
      leftController.quaternion.w
    );

    const position2 = new THREE.Vector3(
      rightController.position.x,
      rightController.position.y,
      rightController.position.z
    );

    const quaternion2 = new THREE.Quaternion(
      rightController.quaternion.x,
      rightController.quaternion.y,
      rightController.quaternion.z,
      rightController.quaternion.w
    );

    user.right.mesh.position.copy(position2);
    user.right.mesh.setRotationFromQuaternion(quaternion2);
    user.left.mesh.position.copy(position1);
    user.left.mesh.setRotationFromQuaternion(quaternion1);
  }
}

export function removeUserFromScene(id) {
  const userIndex = userMeshes.findIndex((user) => user.id === id);

  if (userIndex >= 0) {
    scene.remove(userMeshes[userIndex].avatar);
    clearGroup(userMeshes[userIndex].avatar);

    scene.remove(userMeshes[userIndex].right.mesh);
    clearGroup(userMeshes[userIndex].right.mesh);

    scene.remove(userMeshes[userIndex].left.mesh);
    clearGroup(userMeshes[userIndex].left.mesh);

    userMeshes.splice(userIndex, 1);
  }
}

export async function addElement(data) {
  const transparent = data.element.name === "map";
  const element = await createElement(data.element.url, transparent);
  element.scale.set(data.element.scale, data.element.scale, data.element.scale);
  element.position.set(data.element.position.x, data.element.position.y, data.element.position.z);
  const quaternion = new THREE.Quaternion(
    data.element.quaternion.x,
    data.element.quaternion.y,
    data.element.quaternion.z,
    data.element.quaternion.w
  );
  element.setRotationFromQuaternion(quaternion);

  scene.add(element);
  const elementProfile = {
    mesh: element,
    ...data.element,
  };
  elementMeshes.push(elementProfile);
}

function getElementById(id) {
  const element = elementMeshes.find((el) => el.id === id);
  return element;
}

export function updateSlide(data) {
  const texture = textureLoader.load(data.url);
  texture.colorSpace = THREE.SRGBColorSpace;
  slideMat.map = texture;
  slideMat.needsUpdate = true;
}

export function startVRSession() {
  async function onSessionStarted(session) {
    isVr = true;
    await renderer.xr.setSession(session);
  }
  const sessionInit = {
    optionalFeatures: ["local-floor", "bounded-floor", "hand-tracking", "layers"],
  };
  navigator.xr.requestSession("immersive-vr", sessionInit).then(onSessionStarted);
}

export function startARSession() {
  async function onSessionStarted(session) {
    environment.visible = false;
    await renderer.xr.setSession(session);
  }
  const sessionInit = {
    optionalFeatures: ["local-floor", "bounded-floor", "hand-tracking", "layers", "unbounded"],
  };
  navigator.xr.requestSession("immersive-ar", sessionInit).then(onSessionStarted);
}

export function endVRSession() {
  const state = store.getState();
  const isInVR = selectisInVR(state);
  if (isInVR) {
    renderer.xr.getSession().end();
  }
}

function onKeyDown(event) {
  speed = 50;
  switch (event.code) {
    case "ArrowUp":
    case "KeyW":
      moveForward = true;
      break;

    case "ArrowLeft":
    case "KeyA":
      moveLeft = true;
      break;

    case "ArrowDown":
    case "KeyS":
      moveBackward = true;
      break;

    case "ArrowRight":
    case "KeyD":
      moveRight = true;
      break;
  }
}

function onKeyUp(event) {
  switch (event.code) {
    case "ArrowUp":
    case "KeyW":
      moveForward = false;
      break;

    case "ArrowLeft":
    case "KeyA":
      moveLeft = false;
      break;

    case "ArrowDown":
    case "KeyS":
      moveBackward = false;
      break;

    case "ArrowRight":
    case "KeyD":
      moveRight = false;
      break;
  }
}

function handleExport() {
  scene.add(dummyObj);
  // dummyObj.rotateY(Math.PI);

  let string = "";
  elementMeshes.forEach((element) => {
    const tmpVector = new THREE.Vector3();
    const position = element.mesh.getWorldPosition(tmpVector);
    const tmpQuaternion = new THREE.Quaternion();
    const quaternion = element.mesh.getWorldQuaternion(tmpQuaternion);
    string += `${element.name}\n
        {x:${position.x.toFixed(2)}, y:${position.y.toFixed(2)}, z:${position.z.toFixed(2)}}\n
        {x:${quaternion._x.toFixed(2)}, y:${quaternion._y.toFixed(2)}, z:${quaternion._z.toFixed(
      2
    )}, w:${quaternion._w.toFixed(2)}}\n\n`;
  });
  console.log(string);
}

function onMouseDown(event) {
  // Handle only primary button.
  if (event.button !== 0) {
    return;
  }

  const state = store.getState();
  const isShowingInfoModal = selectIsShowingInfoModal(state);
  const isShowingOverlay = selectIsShowingHelpOverlay(state);

  if (isShowingInfoModal && event.target.id === "app") {
    store.dispatch(sessionSlice.actions.toggleInfoModal());
  }

  if (isShowingInfoModal || isShowingOverlay) {
    return;
  }

  isMouseDown = true;
  previousMouseEvent.screenX = event.screenX;
  previousMouseEvent.screenY = event.screenY;
  showGrabbingCursor();
}

function onMouseMove(event) {
  let movementX;
  let movementY;

  // Not dragging
  if (!isMouseDown) {
    return;
  }

  movementX = event.screenX - previousMouseEvent.screenX;
  movementY = event.screenY - previousMouseEvent.screenY;

  previousMouseEvent.screenX = event.screenX;
  previousMouseEvent.screenY = event.screenY;

  euler.setFromQuaternion(camera.quaternion);

  euler.y -= movementX * 0.002;
  euler.x -= movementY * 0.002;

  euler.x = Math.max(_PI_2 - maxPolarAngle, Math.min(_PI_2 - minPolarAngle, euler.x));

  camera.quaternion.setFromEuler(euler);
}

function onMouseUp(event) {
  isMouseDown = false;
  hideGrabbingCursor();
}

function onTouchStart(event) {
  if (event.touches.length !== 1) {
    return;
  }

  const state = store.getState();
  const isShowingInfoModal = selectIsShowingInfoModal(state);
  const isShowingOverlay = selectIsShowingHelpOverlay(state);

  if (isShowingInfoModal && event.target.id === "app") {
    store.dispatch(sessionSlice.actions.toggleInfoModal());
  }

  if (isShowingInfoModal || isShowingOverlay) {
    return;
  }

  touchStart = {
    x: event.touches[0].pageX,
    y: event.touches[0].pageY,
  };

  touchStarted = true;
}

function onTouchEnd(event) {
  touchStarted = false;
}

function onTouchMove(event) {
  let movementX;
  let movementY;

  // Not dragging
  if (!touchStarted) {
    return;
  }

  movementX = event.touches[0].pageX - touchStart.x;
  movementY = event.touches[0].pageY - touchStart.y;

  touchStart.x = event.touches[0].pageX;
  touchStart.y = event.touches[0].pageY;

  euler.setFromQuaternion(camera.quaternion);

  euler.y -= movementX * 0.002;
  euler.x -= movementY * 0.002;

  euler.x = Math.max(_PI_2 - maxPolarAngle, Math.min(_PI_2 - minPolarAngle, euler.x));

  camera.quaternion.setFromEuler(euler);
}

function showGrabbingCursor() {
  const app = document.getElementById("app");
  app.style.cursor = "grabbing";
}

function hideGrabbingCursor() {
  const app = document.getElementById("app");
  app.style.cursor = "";
}

export function enableWasd() {
  controls.enabled = false;
  isWasdEnabled = true;
  camera.position.set(0, 1.6, 0);
  camera.lookAt(0, 1.6, -1);
  window.addEventListener("mousedown", onMouseDown, false);
  window.addEventListener("mousemove", onMouseMove, false);
  window.addEventListener("mouseup", onMouseUp, false);
  window.addEventListener("touchstart", onTouchStart, false);
  window.addEventListener("touchmove", onTouchMove, false);
  window.addEventListener("touchend", onTouchEnd, false);
}

export function disableWasd(autoRotate = true) {
  if (controls) {
    controls.enabled = true;
    controls.autoRotate = autoRotate;
  }
  isWasdEnabled = false;
  camera?.position.set(0, 1.6, 3);
  controls?.target.set(0, 1.3, -0.3);
  window.removeEventListener("mousedown", onMouseDown);
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", onMouseUp);
  window.removeEventListener("touchstart", onTouchStart);
  window.removeEventListener("touchmove", onTouchMove);
  window.removeEventListener("touchend", onTouchEnd);
}

export function updateCameraControls({ isOrbitControls }) {
  console.log(isOrbitControls)
  if (!controls.enabled && isOrbitControls) {
    disableWasd(false);
    scene.background = new THREE.Color("black");
    environment.visible = false;
    controls.enablePan = true;
    controls.enableRotate = true;
    controls.enableZoom = true;
    camera?.position.set(0, 1.6, 1);
  } else {
    enableWasd();
    scene.background = new THREE.Color("skyblue");
    environment.visible = true;
    controls.enableRotate = false;
    controls.enablePan = false;
    controls.enableZoom = false;
  }
}

export function updatePositionFromJoystick(vector) {
  speed = 35 * Math.sqrt(vector.x * vector.x + vector.y * vector.y);
  if (vector.x > 0) {
    moveRight = true;
    moveLeft = false;
  } else {
    moveLeft = true;
    moveRight = false;
  }

  if (vector.y > 0) {
    moveForward = true;
    moveBackward = false;
  } else {
    moveBackward = true;
    moveForward = false;
  }
}

export function joystickUpdateEnd() {
  moveRight = false;
  moveLeft = false;
  moveBackward = false;
  moveForward = false;
}

export function renderPdb(pdb, atomData) {
  const state = store.getState();
  const stickRadiusScale = selectStickRadiusScale(state);

  atoms = new THREE.InstancedMesh(sphereGeometry, bondMaterial, pdb.positions.length);
  atoms.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  atoms.frustumCulled = false;

  for (let i = 0; i < atoms.count; i++) {
    const matrix = new THREE.Matrix4();

    const scale = new THREE.Vector3();
    scale.x = scale.y = scale.z = atomData[i].scale;

    const rotation = new THREE.Euler();
    const quaternion = new THREE.Quaternion();
    quaternion.setFromEuler(rotation);

    const position = new THREE.Vector3();
    position.x = atomData[i].x;
    position.y = atomData[i].y;
    position.z = atomData[i].z;
    matrix.compose(position, quaternion, scale);

    atoms.setMatrixAt(i, matrix);
    const color = new THREE.Color(pdb.colors[i]);
    atoms.setColorAt(i, color);
  }
  scene.add(atoms);

  bondData = createSticks(pdb);

  const cylinderGeometry = new THREE.CylinderGeometry(stickRadiusScale, stickRadiusScale, 1, 16);

  bonds = new THREE.InstancedMesh(cylinderGeometry, bondMaterial, bondData.length * 2);
  bonds.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  bonds.frustumCulled = false;

  let bondIndex = 0;

  bondData.forEach(function (bond) {
    //Set colors from starting values
    bonds.setColorAt(bondIndex, new THREE.Color(bond.colors[0]));
    bondIndex++;
    bonds.setColorAt(bondIndex, new THREE.Color(bond.colors[1]));
    bondIndex++;
    const atom1Index = bond.atomA;
    const atom2Index = bond.atomB;

    for (let i = 0; i < atoms.count; i++) {
      if (i == atom1Index || i == atom2Index) bond.cylinderFactor = pdb.stickRadius[i];
    }
  });

  updateBonds();
  scene.add(bonds);
}

export function updateAtomColors(newColors) {
  //Update spheres colors
  for (let i = 0; i !== atoms.count; i++) {
    const color = new THREE.Color(newColors[i]);
    atoms.setColorAt(i, color);
  }
  atoms.instanceColor.needsUpdate = true;

  //Update sticks colors
  let stickCount = 0;

  bondData.forEach(function (bond, index) {
    const atom1Index = bond.atomA;
    const atom2Index = bond.atomB;

    const somecolor = new THREE.Color();
    atoms.getColorAt(atom1Index, somecolor);
    bonds.setColorAt(stickCount, somecolor);

    stickCount++;

    const somecolor2 = new THREE.Color();
    atoms.getColorAt(atom2Index, somecolor2);
    bonds.setColorAt(stickCount, somecolor2);

    stickCount++;
  });
  bonds.instanceColor.needsUpdate = true;
}

export function renderAtomsUpdate(data) {
  for (let i = 0; i !== atoms.count; i++) {
    const atomRadius = data[i].scale;
    const scale = new THREE.Vector3();

    scale.x = scale.y = scale.z = atomRadius;

    const rotation = new THREE.Euler();
    const quaternion = new THREE.Quaternion();
    quaternion.setFromEuler(rotation);

    const position = new THREE.Vector3();
    position.x = data[i].x;
    position.y = data[i].y;
    position.z = data[i].z;

    const matrix = new THREE.Matrix4();
    matrix.compose(position, quaternion, scale);
    atoms.setMatrixAt(i, matrix);
  }

  atoms.instanceMatrix.needsUpdate = true;
  updateBonds();
  bonds.instanceMatrix.needsUpdate = true;
}

function updateBonds() {
  const state = store.getState();
  const renderScale = selectRenderScale(state);

  let stickCount = 0;

  bondData.forEach(function (bond, index) {
    const atom1Index = bond.atomA;
    const atom2Index = bond.atomB;

    const point1 = new THREE.Vector3();
    const atomMatrix = new THREE.Matrix4();
    atoms.getMatrixAt(atom1Index, atomMatrix);
    point1.setFromMatrixPosition(atomMatrix);

    const point3 = new THREE.Vector3();
    const atomMatrix2 = new THREE.Matrix4();
    atoms.getMatrixAt(atom2Index, atomMatrix2);
    point3.setFromMatrixPosition(atomMatrix2);

    const point2 = new THREE.Vector3();
    point2.lerpVectors(point1, point3, 0.5);

    const vec = point1.clone();
    vec.sub(point2);
    const height = vec.length();
    vec.normalize();

    const width = (bond.isDouble ? 1.3 : 1) * renderScale * bond.cylinderFactor;

    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), vec);

    const stickPosition = new THREE.Vector3();
    stickPosition.lerpVectors(point2, point1, 0.5);

    const scale = new THREE.Vector3(width, height, width);

    tmpMatrix.compose(stickPosition, quaternion, scale);

    bonds.setMatrixAt(stickCount, tmpMatrix);

    /*const somecolor = new THREE.Color();
    atoms.getColorAt(atom1Index, somecolor);
    bonds.setColorAt(stickCount, somecolor);*/

    stickCount++;

    const vec2 = point3.clone();
    vec2.sub(point2);
    const height2 = vec2.length();
    vec2.normalize();

    const quaternion2 = new THREE.Quaternion();
    quaternion2.setFromUnitVectors(new THREE.Vector3(0, 1, 0), vec2);

    const stickPosition2 = new THREE.Vector3();
    stickPosition2.lerpVectors(point2, point3, 0.5);

    const scale2 = new THREE.Vector3(width, height2, width);

    tmpMatrix.compose(stickPosition2, quaternion2, scale2);

    bonds.setMatrixAt(stickCount, tmpMatrix);

    /*const somecolor2 = new THREE.Color();
    atoms.getColorAt(atom2Index, somecolor2);
    bonds.setColorAt(stickCount, somecolor2);*/

    stickCount++;
  });
  if (bonds.count > 0) {
    bonds.instanceColor.needsUpdate = true;
  }
}

export function updateStickRadius(data) {
  const newRadii = data.newStickRadius;

  bondData.forEach(function (bond, index) {
    const atom1Index = bond.atomA;
    const atom2Index = bond.atomB;

    for (let i = 0; i < newRadii.length; i++) {
      if (i == atom1Index || i == atom2Index) bond.cylinderFactor = newRadii[i];
    }
  });
}

function createSticks(pdb) {
  const bondKeys = Object.keys(pdb.bonds);
  const bonds = [];

  bondKeys.forEach(function (atom, atomIndex) {
    //point1 is the first atom (i), point3 is the second atom (j)
    //point2 is at the center in-between atoms i and j
    //then the first half of the bond is from sphere 1 to 2 and the
    //second half of the bond is from point2 to point3

    const point1 = pdb.positions[atomIndex];

    pdb.bonds[atom].forEach(function (bondedAtom) {
      const bondedAtomIndex = bondKeys.indexOf(bondedAtom);

      const point2 = new THREE.Vector3();
      const point3 = pdb.positions[bondedAtomIndex];

      point2.lerpVectors(point1, point3, 0.5);

      let radius = SIMPLE;
      const atom1Bonds = pdb.allBonds[atom].length;
      const atom2Bonds = pdb.allBonds[bondedAtom].length;

      //     /******************  Bonde rules for C *************************/
      if (pdb.elements[atomIndex] === "C" && pdb.elements[bondedAtomIndex] === "C") {
        if (atom1Bonds === 4 && atom2Bonds === 4) {
          radius = SIMPLE;
        }

        if (atom1Bonds === 3 && atom2Bonds === 3) {
          radius = DOUBLE;
        }

        if (atom1Bonds === 2 && atom2Bonds === 2) {
          radius = TRIPLE;
        }
      }

      //     /******************  Bonde rules for O *************************/

      // One of both atoms is O and have 1 bonded atom (TERMINAL) => DOUBLE
      // Otherwise is simple
      if (
        (pdb.elements[atomIndex] === "O" && atom1Bonds === 1) ||
        (pdb.elements[bondedAtomIndex] === "O" && atom2Bonds === 1)
      ) {
        radius = DOUBLE;
      }

      /******************  Bonde rules for N *************************/

      // One atom is N and have 4 bonded atoms
      if (
        (pdb.elements[atomIndex] === "N" && atom1Bonds === 4) ||
        (pdb.elements[bondedAtomIndex] === "N" && atom2Bonds === 4)
      ) {
        radius = SIMPLE;
      }

      // One atom is N with 3 bonded atoms
      // The other atom is C with 4 bonded atoms
      if (
        (pdb.elements[atomIndex] === "N" &&
          atom1Bonds === 3 &&
          pdb.elements[bondedAtomIndex] === "C" &&
          atom2Bonds === 4) ||
        (pdb.elements[atomIndex] === "C" &&
          atom1Bonds === 4 &&
          pdb.elements[bondedAtomIndex] === "N" &&
          atom2Bonds === 3)
      ) {
        radius = SIMPLE;
      }

      // One atom is N with 3 bonded atoms
      // The other atom is O with 2 bonded atoms
      if (
        (pdb.elements[atomIndex] === "N" &&
          atom1Bonds === 3 &&
          pdb.elements[bondedAtomIndex] === "O" &&
          atom2Bonds === 2) ||
        (pdb.elements[atomIndex] === "O" &&
          atom1Bonds === 2 &&
          pdb.elements[bondedAtomIndex] === "N" &&
          atom2Bonds === 3)
      ) {
        radius = SIMPLE;
      }

      // One atom is N with 3 bonded atoms
      // The other atom is C with 3 bonded atoms
      if (
        (pdb.elements[atomIndex] === "N" &&
          atom1Bonds === 3 &&
          pdb.elements[bondedAtomIndex] === "C" &&
          atom2Bonds === 3) ||
        (pdb.elements[atomIndex] === "C" &&
          atom1Bonds === 3 &&
          pdb.elements[bondedAtomIndex] === "N" &&
          atom2Bonds === 3)
      ) {
        radius = DOUBLE;
      }

      // One atom is N with 2 bonded atoms
      // The other atom is C with 3 bonded atoms
      if (
        (pdb.elements[atomIndex] === "N" &&
          atom1Bonds === 2 &&
          pdb.elements[bondedAtomIndex] === "C" &&
          atom2Bonds === 3) ||
        (pdb.elements[atomIndex] === "C" &&
          atom1Bonds === 3 &&
          pdb.elements[bondedAtomIndex] === "N" &&
          atom2Bonds === 2)
      ) {
        radius = DOUBLE;
      }

      // One atom is N and have 1 bonded atom (TERMINAL) => TRIPLE
      if (
        (pdb.elements[atomIndex] === "N" && atom1Bonds === 1) ||
        (pdb.elements[bondedAtomIndex] === "N" && atom2Bonds === 1)
      ) {
        radius = TRIPLE;
      }

      // Both atoms are N with two bonded atoms
      if (
        pdb.elements[atomIndex] === "N" &&
        atom1Bonds === 2 &&
        pdb.elements[bondedAtomIndex] === "N" &&
        atom2Bonds === 2
      ) {
        radius = DOUBLE;
      }

      // One of both atoms is H and have 1 bonded atom (TERMINAL) => SIMPLE
      // Otherwise is simple
      if (
        (pdb.elements[atomIndex] === "H" && atom1Bonds === 1) ||
        (pdb.elements[bondedAtomIndex] === "H" && atom2Bonds === 1)
      ) {
        radius = SIMPLE;
      }

      bonds.push({
        atomA: atomIndex,
        atomB: bondedAtomIndex,
        colors: [pdb.colors[atomIndex], pdb.colors[bondedAtomIndex]],
        isDouble: radius === DOUBLE,
        cylinderFactor: 1,
      });
    });
  });
  return bonds;
}

export function addRubber3D(newRubber) {
  const mesh = new THREE.InstancedMesh(rubberGeometry, rubberMaterial, 9);
  let newRubber3D = {
    ...newRubber,
    mesh,
  };
  mesh.frustumCulled = false;
  scene.add(mesh);
  rubberMeshes.push(newRubber3D);
}

export function removeRubber3D(id) {
  const index = rubberMeshes.findIndex((rubber) => rubber.id === id);

  if (index >= 0) {
    scene.remove(rubberMeshes[index].mesh);
    rubberMeshes[index].mesh.dispose();
    rubberMeshes.splice(index, 1);
  }
}

export function removeAllRubbers() {
  rubberMeshes.forEach((rubber) => {
    scene.remove(rubber.mesh);
    rubber.mesh.dispose();
  });
  rubberMeshes = [];
}

function updateRubbers() {
  const state = store.getState();
  const renderScale = selectRenderScale(state);

  rubberMeshes.forEach((rubber) => {
    // P0 is like this for non covalent and should be from the controller for USER Grabbing
    const p0 = new THREE.Vector3();

    if (rubber.type === "NON_COVALENT_INTERACTION") {
      const atomMatrix1 = new THREE.Matrix4();
      atoms.getMatrixAt(rubber.pointA, atomMatrix1);
      p0.setFromMatrixPosition(atomMatrix1);
    } else if (rubber.type === "USER_PULLING") {
      const myPoint = getPivotFromRubber(rubber);
      p0.set(myPoint.x, myPoint.y, myPoint.z);
    }

    // P1 always corresponds to an ATOM
    const p1 = new THREE.Vector3();
    const atomMatrix2 = new THREE.Matrix4();
    atoms.getMatrixAt(rubber.pointB, atomMatrix2);
    p1.setFromMatrixPosition(atomMatrix2);

    for (let index = 0; index < 9; index++) {
      const position = new THREE.Vector3();
      position.lerpVectors(p0, p1, index / 10);
      const quaternion = new THREE.Quaternion();
      const scaleValue = 2 * renderScale;
      const scale = new THREE.Vector3(scaleValue, scaleValue, scaleValue);
      const matrix = new THREE.Matrix4();
      matrix.compose(position, quaternion, scale);
      rubber.mesh.setMatrixAt(index, matrix);
    }
    rubber.mesh.instanceMatrix.needsUpdate = true;
  });
}

function getPivotFromRubber(rubber) {
  const state = store.getState();
  const myId = selectMyId(state);

  const myPivot = new THREE.Vector3(0, 0, 0);

  // I am pulling this atom
  if (rubber.pointA.userId === myId) {
    if (isRHand && isLHand) {
      const myHand = rubber.pointA.controllerName === "hand1" ? hand1 : hand2;
      myHand.joints["index-finger-tip"].getWorldPosition(myPivot);
    } else {
      const myController = rubber.pointA.controllerName === "controller1" ? controllerGrip1 : controllerGrip2;
      const controllerPivot = myController.getObjectByName("pivot");
      controllerPivot.getWorldPosition(myPivot);
    }
    return myPivot;
  }

  // Someone else is pulling the atom
  const user = userMeshes.find((user) => user.id === rubber.pointA.userId);

  if (user && user.type === "hand") {
    const userHand = rubber.pointA.handedness === "right" ? user.right.bones : user.left.bones;
    const tip = userHand.find((bone) => bone.name === "index-finger-tip");
    myPivot.set(tip.position.x, tip.position.y, tip.position.z);
    return myPivot;
  }

  if (user && user.type === "controller") {
    const userController = rubber.pointA.handedness === "right" ? user.right.mesh : user.left.mesh;
    const controllerPivot = userController.getObjectByName("pivot");
    controllerPivot.getWorldPosition(myPivot);
    return myPivot;
  }
}

function formatInputSources(session) {
  if (!session || !session.inputSources) {
    return [];
  }

  return Array.from(session.inputSources).map((inputSource) => {
    let controllerType = "unknown";

    if (inputSource.hand) {
      controllerType = "hand";
    } else if (inputSource.targetRayMode === "tracked-pointer") {
      controllerType = "controller";
    }

    return {
      controllerType,
      handedness: inputSource.handedness,
    };
  });
}

const formatTrackedSources = (trackedSources) => {
  if (!trackedSources || trackedSources.length === 0) {
    return "No tracked sources available";
  }
  return Array.from(trackedSources)
    .map((source) => source.profiles.join(", "))
    .join(" | ");
};

function threeLettersToFullNames(threeLettersCode) {
  switch (threeLettersCode) {
    case "ALA":
      return "alanine";
    case "CYS":
      return "cysteine";
    case "ASP":
      return "aspartate";
    case "GLU":
      return "glutamate";
    case "PHE":
      return "phenylalanine";
    case "GLY":
      return "glycine";
    case "HIS":
      return "histidine";
    case "ILE":
      return "isoleucine";
    case "LYS":
      return "lysine";
    case "LEU":
      return "leucine";
    case "MET":
      return "methionine";
    case "PRO":
      return "proline";
    case "GLN":
      return "glutamine";
    case "ARG":
      return "arginine";
    case "SER":
      return "serine";
    case "THR":
      return "threonine";
    case "VAL":
      return "valine";
    case "TRP":
      return "tryptophane";
    case "TYR":
      return "tyrosine";
    case "WAT":
      return "water";
    case "TIP":
      return "water";
    case "HOH":
      return "water";
    default:
      return threeLettersCode; //Returns same as input, i.e. not converted to full name
  }
}

function cleanupVignette() {
  if (tunnelVignette) {
    camera.remove(tunnelVignette);
    tunnelVignette.geometry.dispose();
    tunnelVignette.material.dispose();
    tunnelVignette = null;
  }
}

function updateUi(time) {
  const delta = prev == null ? 0 : time - prev;
  prev = time;
  toastRoot.update(delta);
  label1Root.update(delta);
  label2Root.update(delta);
  menuRoot.update(delta);
  menuAnchor.lookAt(camera.position);
}

export function display3DToast(msg) {
  const state = store.getState();
  const isInVR = selectisInVR(state);
  if (!isInVR) {
    return;
  }

  const camPosition = new THREE.Vector3();
  camera.getWorldPosition(camPosition);

  // Get camera's forward direction
  const direction = new THREE.Vector3();
  camera.getWorldDirection(direction);

  const toastPosition = new THREE.Vector3();
  toastPosition.copy(camPosition).add(
    direction
      .multiplyScalar(0.8) // Distance in front of camera
      .add(new THREE.Vector3(0, 0, 0)) // Offset downward
  );
  toastAnchor.position.copy(toastPosition);
  toastAnchor.lookAt(camPosition);
  toastAnchor.visible = true;
  toastMsg.setText(msg);

  setTimeout(() => {
    toastAnchor.visible = false;
  }, 4000);
}

function display3DMenu() {
  const state = store.getState();
  const isInVR = selectisInVR(state);
  if (!isInVR) {
    return;
  }

  const camPosition = new THREE.Vector3();
  camera.getWorldPosition(camPosition);

  const camMatrix = new THREE.Matrix4();
  camMatrix.extractRotation(camera.matrixWorld);
  const offset = new THREE.Vector3(-0.08, 0, -0.35);
  offset.applyMatrix4(camMatrix);

  const menuPosition = new THREE.Vector3();
  menuPosition.copy(camPosition).add(offset);
  menuPosition.y = 1.25;
  menuAnchor.position.copy(menuPosition);
  menuAnchor.lookAt(camPosition);
  menuAnchor.visible = true;
}

function close3dMenu() {
  setTimeout(() => {
    menuAnchor.visible = false;
  }, 300);
}

function update3DLabels() {
  const state = store.getState();
  const pdb = selectPdb(state);
  const renderScale = selectRenderScale(state);

  let distance;
  let atomPos1;
  let atomPos2;
  let atomMsg1;
  let atomMsg2;

  if (pointedAtomR !== null) {
    atomPos1 = new THREE.Vector3();
    const atomMatrix = new THREE.Matrix4();

    atoms.getMatrixAt(pointedAtomR, atomMatrix);
    atomPos1.setFromMatrixPosition(atomMatrix);
    const labelPos1 = new THREE.Vector3(0, 0.03, 0);
    labelPos1.add(atomPos1);

    const camPosition = new THREE.Vector3();
    camera.getWorldPosition(camPosition);

    atomMsg1 = getAtomString(pdb, pointedAtomR);

    label1Anchor.position.copy(labelPos1);
    label1Anchor.lookAt(camPosition);
    label1Anchor.visible = true;
    label1Msg.setText(atomMsg1);
  } else {
    label1Anchor.visible = false;
  }

  if (pointedAtomL !== null) {
    atomPos2 = new THREE.Vector3();
    const atomMatrix = new THREE.Matrix4();

    atoms.getMatrixAt(pointedAtomL, atomMatrix);
    atomPos2.setFromMatrixPosition(atomMatrix);
    const labelPos2 = new THREE.Vector3(0, 0.03, 0);
    labelPos2.add(atomPos2);

    const camPosition = new THREE.Vector3();
    camera.getWorldPosition(camPosition);

    atomMsg2 = getAtomString(pdb, pointedAtomL);

    label2Anchor.position.copy(labelPos2);
    label2Anchor.lookAt(camPosition);
    label2Anchor.visible = true;
    label2Msg.setText(atomMsg2);
  } else {
    label2Anchor.visible = false;
  }

  if (pointedAtomR && pointedAtomL) {
    atomPos1.divideScalar(renderScale);
    atomPos2.divideScalar(renderScale);
    distance = atomPos1.distanceTo(atomPos2);
    const distanceText = `\nd = ${distance.toFixed(2)} A`;
    label1Msg.setText(atomMsg1 + distanceText);
    label2Msg.setText(atomMsg2 + distanceText);
  }
}

function checkPointing() {
  const isPoint1 = isPointing(hand1);
  const isPoint2 = isPointing(hand2);
  let indexTip1 = null;
  let indextip2 = null;

  if (isPoint1) {
    indexTip1 = hand1.joints["index-finger-tip"];
  }
  if (isPoint2) {
    indextip2 = hand2.joints["index-finger-tip"];
  }

  [pointedAtomR, pointedAtomL] = collideMoleculeWithBothHands(indexTip1, indextip2, atoms);
}

function checkButtonsInteraction() {
  checkHandButton();
}
